import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import FormComponent from '../../../../widgets/form/FormComponent/FormComponent';
import { checkUserToken, getFormFields, getFormStyles } from './helpers';
import { LangContext } from '../../../../common/decorators/Language';
import { Portal } from '../../../../hybrid/wrappers';
import { dataSender, getData, navigateV2 } from '../../../../common/utils/router';
import UserContext, { UserProfileContext } from '../../../../common/utils/getContext';
import T from '../../../../common/components/T';
import { API_SERVER_URL, getCountryIdByDomain, setLanguageForEmail } from '../../../../common/utils/utils';
import Loader from '../../../../widgets/plug/Loader/Loader';
import { useRouter } from 'next/router';
import css from '../../../../widgets/agreeModal/AgreeModal.module.scss';
import ViewPdf from '../../../../widgets/viewPdf/ViewPdf';

const RegisterView = ({ setActiveStep, isOfficeRegister, setPartner, setUserData, initValue, setInitValue }) => {
    const { lang, setUserToken, setAlertData, storageLoaded, refer, userToken, localization } = useContext(UserContext);
    const { profileData } = useContext(UserProfileContext) || {};
    const { translation } = useContext(LangContext);
    // const [checkCodeEmail, setCheckCodeEmail] = useState(false);
    const [sponsorData, setSponsorData] = useState(null);
    const interval = useRef();
    const [errors, setErrors] = useState(null);
    const [signDocs, setSignDocs] = useState(null);
    const [allowedSendCustom, setAllowedSendCustom] = useState(false);
    const [userDocuments, setUserDocuments] = useState(null);

    useEffect(() => {
        if (storageLoaded) {
            if (userToken && !profileData) return;
            let country_id;
            const domain = typeof window !== 'undefined' && window.location.host.split('.').pop();
            if (initValue?.country_id) {
                country_id = initValue?.country_id?.geoname_id;
            } else {
                if (userToken) {
                    country_id = profileData?.country?.geoname_id || profileData?.country?.geoname_id;
                } else {
                    if (domain === 'com' || domain === 'cloud') {
                        country_id = localization?.country?.id;
                    } else {
                        country_id = getCountryIdByDomain(domain);
                    }
                }
            }
            if (country_id) {
                getData(
                    `/api/v1/documents/anonymous?filter[country_id]=${country_id}&filter[type]=registration&page[size]=999`,
                    userToken,
                    lang
                ).then((res) => {
                    setUserDocuments(res?.data);
                });
            }
        }
    }, [userToken, initValue?.country_id?.geoname_id, lang, storageLoaded, profileData, localization, isOfficeRegister]);

    const [isRefer, setIsRefer] = useState(false);
    const fields = useMemo(
        () => getFormFields(sponsorData, isRefer, isOfficeRegister, userDocuments),
        [sponsorData, isRefer, isOfficeRegister, userDocuments]
    );
    const formStyles = useMemo(() => getFormStyles(isOfficeRegister), [isOfficeRegister]);

    const searchFunc = useCallback(
        async (text) => {
            getData(`/api/v1/sponsors/${text}`, false, lang)
                .then((res) => {
                    if (res?.data) {
                        setSponsorData({
                            data: {
                                ...res?.data,
                                sponsor_username: res?.data?.username
                            }
                        });
                        setErrors((state) => {
                            const newState = { ...state };
                            delete newState.sponsor_username;
                            return newState;
                        });
                    } else {
                        setErrors((e = {}) => ({
                            ...e,
                            sponsor_username: [
                                <T
                                    key='0'
                                    textName='registration_fields_sponsor_username_not_found-v1'
                                    defaultStr='Пользователь с таким ID не найден.'
                                    page='/store/register'
                                />
                            ]
                        }));
                    }
                })
                .catch(console.warn);
        },
        [lang]
    );

    const search = useCallback(
        (text) => {
            clearTimeout(interval.current);
            interval.current = setTimeout(async () => {
                await searchFunc(text);
            }, 500);
        },
        [searchFunc]
    );

    const afterSend = useCallback(
        (data, _, setAllowedSend) => {
            const scrollToTop = () => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth' // плавна анімація
                });
            };
            setAllowedSend(false);
            // if (data?.data?.email_verification_enabled) {
            //     return setStepData({
            //         component: VerifyEmail,
            //         props: {
            //             setStepData,
            //             data: { ...data, userData }
            //         },
            //         layoutStyle: 'authBlock_lg'
            //     });
            // }
            if (data?.data?.token) {
                setLanguageForEmail(data?.data?.token, lang);
            }
            if (isOfficeRegister) {
                setPartner(data?.data);
                setActiveStep((s) => s + 1);
                scrollToTop();
            } else {
                checkUserToken(
                    data,
                    () => {
                        setActiveStep((s) => s + 1);
                        scrollToTop();
                    },
                    setAllowedSend,
                    setUserToken,
                    setAlertData,
                    translation
                );
            }
        },
        [lang, setAlertData, setUserToken, translation, isOfficeRegister, setPartner]
    );

    const afterError = useCallback((data, status, headers, setFieldsErr, setAllowedSend, modernDataToSend) => {
        setAllowedSend(true);
        if (data?.errors) {
            setErrors(data?.errors);
            if (!!data?.errors && Object.keys(data?.errors).every((k) => k.startsWith('agrees'))) {
                setSignDocs(true);
                const addUserInfoToLink = (link) => {
                    return `${link}?country_id=${modernDataToSend?.country_id}&email=${modernDataToSend?.email}&first_name=${modernDataToSend?.first_name}&last_name=${modernDataToSend?.last_name}`;
                };
                setUserDocuments((state) =>
                    state?.map((doc) => ({
                        ...doc,
                        file: {
                            ...doc?.file,
                            link: {
                                url: addUserInfoToLink(doc?.file?.link),
                                httpHeaders: {
                                    Authorization: `Bearer ${userToken}`
                                }
                            }
                        }
                    }))
                );
            } else {
                setSignDocs(false);
            }
        }
    }, []);

    // const validateEmail = (email) => {
    //     const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    //     return re.test(email);
    // };
    const setFormDataMainFunc = (key, val, setInitValue) => {
        if (typeof val === 'string' && val.length === 0) {
            setInitValue((pre) =>
                Object.keys(pre).reduce((accum, keyFormData) => {
                    if (key !== keyFormData) accum[keyFormData] = pre[keyFormData];
                    return accum;
                }, {})
            );
        } else {
            setInitValue((pre) => {
                return {
                    ...pre,
                    [key]: val
                };
            });
        }
    };

    useEffect(() => {
        setUserData(initValue);
    }, [initValue]);
    useEffect(() => {
        if (storageLoaded && refer) {
            search(refer);
            setIsRefer(true);
            setInitValue({ sponsor_username: refer });
        }
    }, [storageLoaded, refer, search]);

    const setFormDataMain = useCallback((key, val) => {
        setFormDataMainFunc(key, val, setInitValue);
    }, []);
    const router = useRouter();
    // useEffect(() => {
    //     if (initValue?.email && validateEmail(initValue.email)) {
    //         clearTimeout(interval.current);
    //         interval.current = setTimeout(() => {
    //             dataSender(
    //                 `${API_SERVER_URL}/api/v1/users/check-email`,
    //                 'POST',
    //                 {
    //                     email: initValue?.email
    //                 },
    //                 (response) => {
    //                     if (response?.data?.data?.length >= 1) {
    //                         dataSender(
    //                             `${API_SERVER_URL}/api/v1/users/send-registration-code`,
    //                             'POST',
    //                             {
    //                                 email: initValue?.email
    //                             },
    //                             (response) => {
    //                                 if (response?.status == 204) {
    //                                     setCheckCodeEmail(true);
    //                                 } else {
    //                                     setCheckCodeEmail(false);
    //                                 }
    //                             },
    //                             () => {},
    //                             userToken,
    //                             lang
    //                         );
    //                     } else {
    //                         setCheckCodeEmail(false);
    //                     }
    //                 },
    //                 () => {},
    //                 userToken,
    //                 lang
    //             );
    //         }, 500);
    //     }
    // }, [userToken, lang, initValue?.email]);

    // логіка для реєстрації під себе з кабінету
    useEffect(() => {
        if (isOfficeRegister && profileData) {
            setSponsorData({
                data: {
                    sponsor_username: profileData?.username,
                    first_name: profileData?.first_name,
                    last_name: profileData?.last_name
                }
            });
            setInitValue({ sponsor_username: profileData?.username });
        }
    }, [isOfficeRegister, profileData]);

    useEffect(() => {
        if (userToken && !router?.query?.o === '1') navigateV2('/error/403');
    }, [userToken, router]);

    useEffect(() => {
        setFormDataMain('agrees', {});
    }, [initValue?.country_id, setFormDataMain]);
    const sender = useCallback(() => {
        if (!allowedSendCustom) {
            const agreesMap = userDocuments.reduce((acc, doc) => {
                acc[String(doc.id)] = true;
                return acc;
            }, {});

            dataSender(
                isOfficeRegister ? `${API_SERVER_URL}/api/v1/user/register` : `${API_SERVER_URL}/api/v1/register`,
                'POST',
                {
                    ...initValue,
                    country_id: initValue?.country_id?.value,
                    agrees: agreesMap
                },
                (data) => {
                    setAllowedSendCustom(false);
                    setSignDocs(false);
                    afterSend(data?.data, false, setAllowedSendCustom);
                },
                (data, status, headers) => {
                    afterError(data, status, headers, false, setAllowedSendCustom);
                },
                userToken,
                lang
            );
        }
    }, [initValue, allowedSendCustom, userToken, lang, userDocuments, isOfficeRegister]);
    if (refer && !sponsorData) return <Loader />;

    return (
        <div>
            <FormComponent
                sendData={{
                    url: isOfficeRegister ? `${API_SERVER_URL}/api/v1/user/register` : `${API_SERVER_URL}/api/v1/register`,
                    afterSenderSuccess: afterSend,
                    afterSenderError: afterError
                }}
                data={fields}
                setFormDataMain={setFormDataMain}
                formDataMain={initValue}
                captcha={{ alias: 'user::register' }}
                formStyles={formStyles}
                funcs={{
                    sponsor_username: {
                        onChange: (text) => {
                            search(text);
                            return text;
                        }
                    },
                    country_id: {
                        onChange: (value) => {
                            return value?.value || value;
                        }
                    },
                    agrees: {
                        onChange: (value) =>
                            value?.reduce?.((acc, doc) => {
                                if (doc.value) {
                                    acc[doc.id] = true;
                                }
                                return acc;
                            }, {})
                    }
                }}
                groupFormDataError={errors}
                buttons={[
                    {
                        type: 'ok',
                        buttonText: <T textName='further' defaultStr='Далее' page='/store/register' />
                    }
                ]}
            />
            {signDocs ? (
                <Portal>
                    <div className={css.modal}>
                        <p className={css.modalTitle}>
                            <T
                                textName='agree-modal-title-v1'
                                defaultStr='Для продолжения работы необходимо ознакомиться со следующими документами'
                            />
                        </p>

                        <ViewPdf
                            documents={userDocuments}
                            sender={() => {
                                sender();
                            }}
                        />
                    </div>
                </Portal>
            ) : null}
        </div>
    );
};

export default RegisterView;
